import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { auth } from "./services/api";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Board from "./pages/Board";
import Products from "./pages/Products";
import Containers from "./pages/Containers";
import Locations from "./pages/Locations";
import Organize from "./pages/Organize";
import Scanner from "./pages/Scanner";
import EntityDetail from "./pages/EntityDetail";
import CreateEntity from "./pages/CreateEntity";
import ScanRedirect from "./pages/ScanRedirect";
import Trash from "./pages/Trash";
import QRCodes from "./pages/QRCodes";
import Settings from "./pages/Settings";
import Account from "./pages/Account";
import CustomViews from "./pages/CustomViews";
import CustomViewDetail from "./pages/CustomViewDetail";
import CreateCustomView from "./pages/CreateCustomView";
import EmailScanning from "./pages/EmailScanning";
import ItemsSold from "./pages/ItemsSold";
import ItemsListed from "./pages/ItemsListed";
import SalesAnalytics from "./pages/SalesAnalytics";
import Sidebar from "./components/Sidebar";
import BottomNav from "./components/BottomNav";
import ToastHost from "./components/Toast";
import CommandPalette from "./components/CommandPalette";
import QuickAddModal from "./components/QuickAddModal";
import { useSidebarCollapsed } from "./hooks/usePersistentToggle";

function AppContent({ onLogout }) {
  const { commandPaletteOpen, openCommandPalette, closeCommandPalette } =
    useApp();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();
  // Global keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        openCommandPalette();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [openCommandPalette]);

  return (
    <div className={`app-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
      />
      <BottomNav />
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={closeCommandPalette}
        onQuickAdd={() => setQuickAddOpen(true)}
      />
      <QuickAddModal
        isOpen={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
      />
      <div className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard onLogout={onLogout} />} />
          <Route path="/products" element={<Products />} />
          <Route path="/board" element={<Board />} />
          <Route path="/containers" element={<Containers />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/organize" element={<Organize />} />
          <Route path="/scan" element={<Scanner />} />
          <Route path="/scan/:qrCode" element={<ScanRedirect />} />
          <Route path="/entity/:id" element={<EntityDetail />} />
          <Route path="/create" element={<CreateEntity />} />
          <Route path="/trash" element={<Trash />} />
          <Route path="/qr-codes" element={<QRCodes />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/account" element={<Account onLogout={onLogout} />} />
          <Route path="/views" element={<CustomViews />} />
          <Route path="/views/create" element={<CreateCustomView />} />
          <Route path="/views/:id" element={<CustomViewDetail />} />
          <Route path="/email-scanning" element={<EmailScanning />} />
          <Route path="/sales/sold" element={<ItemsSold />} />
          <Route path="/sales/listed" element={<ItemsListed />} />
          <Route path="/sales/analytics" element={<SalesAnalytics />} />
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function UnauthenticatedApp({ onLogin }) {
  const location = useLocation();

  // Build redirect URL with the intended path as query param
  const redirectTo =
    location.pathname !== "/login"
      ? `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`
      : "/login";

  return (
    <Routes>
      <Route path="/login" element={<Login onLogin={onLogin} />} />
      <Route path="*" element={<Navigate to={redirectTo} replace />} />
    </Routes>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setIsAuthenticated(!!token);
    setLoading(false);
  }, []);

  const handleLogout = () => {
    // Best-effort server-side revoke of the refresh token; ignore failures
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      auth.logout(refreshToken).catch(() => {});
    }
    // Remove only session/cache keys — a blanket clear() used to wipe eBay
    // credentials and description templates stored alongside them.
    ["access_token", "refresh_token", "user", "products", "containers", "locations", "counts", "trash"].forEach(
      (k) => localStorage.removeItem(k),
    );
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {/* Keyed on auth state so logout remounts the provider, discarding the
          previous account's in-memory products/containers/counts */}
      <AppProvider key={isAuthenticated ? "authed" : "anon"}>
        <ToastHost />
        {isAuthenticated ? (
          <AppContent onLogout={handleLogout} />
        ) : (
          <UnauthenticatedApp onLogin={() => setIsAuthenticated(true)} />
        )}
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
